import { PDFDownloadLink } from '@react-pdf/renderer';
import { PDF_FILENAME } from 'constants/general';
import { HabitPackFormat, PackPickerVariant } from 'enums';
import { t } from 'services/i18n.service';
import { useAppSelectorShallow } from 'store/hooks';
import { getPackIds } from 'utils/helpers';
import HabitPackActionButton from 'components/common/buttons/HabitPackActionButton';
import PackMarketplaceControls from 'components/common/buttons/PackMarketplaceControls';
import PackPreviewActionsWrapper from './PackPreviewActionsWrapper';
import HabitPackPdfDocument from 'dashboard/marketplace/habitPackPdf/HabitPdfDocument';
import { habitPackAuthenticatedActionsSelector } from 'store/reducer/habit-pack/selectors';
import { ATTRIB } from 'constants/test';
import { useState } from 'react';
import {
  canInstallOrUninstallMarketplaceEntity,
  canManageMarketplaceEntity,
  canUseMarketplaceAuthActions,
  isFocusOnlyHabitPack
} from 'utils/marketplace.util';

const AdminManageActions = () => (
  <>
    <HabitPackActionButton
      text={t('market.edit')}
      data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_EDIT}
    />
    <HabitPackActionButton
      text={t('market.delete')}
      data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_DELETE}
    />
  </>
);

const OwnerActions = () => {
  const { currentViewedPack, installedHabitPacks, userId } =
    useAppSelectorShallow(habitPackAuthenticatedActionsSelector);

  const installedPacksIds = getPackIds(installedHabitPacks);
  const userHasPackInstalled =
    currentViewedPack?.id && installedPacksIds.includes(currentViewedPack.id);

  const isPlayingStandaloneRoutineAllowed =
    userHasPackInstalled &&
    currentViewedPack?.pack_type === HabitPackFormat.STANDALONE;
  const [hasDownloadedPdf, setHasDownloadedPdf] = useState(false);

  const pdfButtonText = (loading: boolean, downloaded: boolean) => {
    if (loading) {
      return t('loading_document');
    }
    if (downloaded) {
      return t('downloaded');
    }
    return t('download_pdf');
  };

  return (
    <>
      <HabitPackActionButton
        text={t('market.edit')}
        data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_EDIT}
      />
      <HabitPackActionButton
        text={t('market.delete')}
        data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_DELETE}
      />
      <HabitPackActionButton
        text={t('market.duplicate')}
        data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_DUPLICATE}
      />
      <PDFDownloadLink
        className='md:hidden ~text-xs/base font-semibold ~px-8/10 py-2 bg-blue-700 text-white rounded-md'
        onClick={() => setHasDownloadedPdf(true)}
        document={<HabitPackPdfDocument habitPack={currentViewedPack} />}
        fileName={PDF_FILENAME.SETTINGS}>
        {({ loading }) => <>{pdfButtonText(loading, hasDownloadedPdf)}</>}
      </PDFDownloadLink>
      <HabitPackActionButton
        text={t('market.generate_pdf')}
        data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_GENERATE_PDF}
      />
      {isPlayingStandaloneRoutineAllowed && (
        <HabitPackActionButton
          text={t('market.play')}
          data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_PLAY}
        />
      )}
      {currentViewedPack && userId === currentViewedPack.user_id && (
        <PackMarketplaceControls habitPack={currentViewedPack} />
      )}
    </>
  );
};

const PackPreviewHabitPackActions = ({
  variant
}: {
  variant: PackPickerVariant;
}) => {
  const { currentViewedPack, installedHabitPacks, userId, isUserAdmin } =
    useAppSelectorShallow(habitPackAuthenticatedActionsSelector);

  const canUseAuthActions = canUseMarketplaceAuthActions(userId);
  const canInstallOrUninstall =
    canInstallOrUninstallMarketplaceEntity(userId);
  const installedPacksIds = getPackIds(installedHabitPacks);
  const userHasPackInstalled =
    currentViewedPack?.id && installedPacksIds.includes(currentViewedPack.id);
  const isOwner = Boolean(
    userId && currentViewedPack && userId === currentViewedPack.user_id
  );
  const canManage = canManageMarketplaceEntity(isOwner, isUserAdmin);
  const focusOnly = currentViewedPack
    ? isFocusOnlyHabitPack(currentViewedPack)
    : false;

  const renderSecondaryActions = () => {
    if (!canUseAuthActions) {
      return (
        variant === PackPickerVariant.MARKETPLACE && (
          <HabitPackActionButton
            text={t('market.generate_pdf')}
            data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_GENERATE_PDF}
          />
        )
      );
    }

    const isListVariant =
      variant === PackPickerVariant.MY_PACKS ||
      variant === PackPickerVariant.MARKETPLACE;

    if (isListVariant && isOwner) {
      return <OwnerActions />;
    }
    if (isListVariant && isUserAdmin && !isOwner) {
      return <AdminManageActions />;
    }
    if (variant === PackPickerVariant.MARKETPLACE && !canManage) {
      return (
        <>
          <HabitPackActionButton
            text={t('market.duplicate')}
            data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_DUPLICATE}
          />
          <HabitPackActionButton
            text={t('market.generate_pdf')}
            data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_GENERATE_PDF}
          />
        </>
      );
    }
    return null;
  };

  return (
    <PackPreviewActionsWrapper>
      {variant === PackPickerVariant.MARKETPLACE &&
        !focusOnly &&
        canInstallOrUninstall && (
          <>
            {userHasPackInstalled ? (
              <HabitPackActionButton
                text={t('market.uninstall')}
                data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_UNINSTALL}
              />
            ) : (
              <HabitPackActionButton
                text={t('market.install')}
                data-testid={ATTRIB.DATA_TESTID.HABIT_PACK_ACTION_INSTALL}
              />
            )}
          </>
        )}
      {renderSecondaryActions()}
    </PackPreviewActionsWrapper>
  );
};

export default PackPreviewHabitPackActions;
